import React, { useState } from "react";
import {
  Modal,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { ResolveMastersResponse } from "../../../types/contract";

interface MasterConflictModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  masterConflictData: ResolveMastersResponse | null;
}

const MasterConflictModal: React.FC<MasterConflictModalProps> = ({
  open,
  onClose,
  onConfirm,
  masterConflictData,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleClose = () => {
    setExpanded(false);
    onClose();
  };

  const handleConfirm = () => {
    setExpanded(false);
    onConfirm();
  };

  if (!masterConflictData) return null;

  const owner = masterConflictData.owner_match;
  const building = masterConflictData.building_match;

  return (
    <Modal open={open} onClose={handleClose}>
      <Card
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 520,
          maxHeight: "85vh",
          overflowY: "auto",
          p: 3,
          borderRadius: "12px",
          outline: "4px solid rgba(115, 169, 244, 1)",
          outlineOffset: "-6px",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* タイトル */}
          <Box
            sx={{
              mb: 2,
              pb: 1,
              borderBottom: "2px solid rgba(115, 169, 244, 1)",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ textAlign: "center", color: "rgba(64, 144, 255, 1)", fontWeight: "bold" }}
            >
              同名の既存データが見つかりました
            </Typography>
          </Box>

          {/* 説明文 */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              bgcolor: "rgba(33, 150, 243, 0.06)",
              borderRadius: 1,
              border: "1px solid rgba(33, 150, 243, 0.2)",
            }}
          >
            <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.7 }}>
              {owner && (
                <>
                  家主名 <strong>「{owner.name}」</strong> はすでに登録されています。
                  <br />
                </>
              )}
              {building && (
                <>
                  建物名 <strong>「{building.name}」</strong> はすでに登録されています。
                  <br />
                </>
              )}
              確定すると、この部屋を以下の既存データに紐づけます。
            </Typography>
          </Box>

          {/* 既存データのサマリー */}
          {owner && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                既存の家主情報
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "35%", py: 0.5 }}>家主名</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{owner.name || "-"}</TableCell>
                  </TableRow>
                  {owner.address1 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>住所</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{owner.address1}</TableCell>
                    </TableRow>
                  )}
                  {owner.home_phone && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>固定電話</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{owner.home_phone}</TableCell>
                    </TableRow>
                  )}
                  {owner.mobile_phone && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>携帯電話</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{owner.mobile_phone}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          {building && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" gutterBottom>
                既存の建物情報
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold", width: "35%", py: 0.5 }}>建物名</TableCell>
                    <TableCell sx={{ py: 0.5 }}>{building.name || "-"}</TableCell>
                  </TableRow>
                  {building.address1 && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>住所</TableCell>
                      <TableCell sx={{ py: 0.5 }}>{building.address1}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          )}

          {/* 全フィールド展開 */}
          <Box sx={{ mt: 1 }}>
            <Button
              size="small"
              variant="text"
              startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setExpanded(!expanded)}
              sx={{ color: "text.secondary", fontSize: "0.75rem" }}
            >
              {expanded ? "詳細を閉じる" : "詳細を確認する"}
            </Button>
          </Box>

          <Collapse in={expanded}>
            <Box sx={{ mt: 1 }}>
              {owner && (
                <Table size="small">
                  <TableBody>
                    {owner.name_kana && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", width: "35%", py: 0.5 }}>家主名カナ</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{owner.name_kana}</TableCell>
                      </TableRow>
                    )}
                    {owner.postal_code && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>郵便番号</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{owner.postal_code}</TableCell>
                      </TableRow>
                    )}
                    {owner.address2 && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>住所2</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{owner.address2}</TableCell>
                      </TableRow>
                    )}
                    {owner.bank_1_account_number && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>銀行1口座番号</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{owner.bank_1_account_number}</TableCell>
                      </TableRow>
                    )}
                    {owner.bank_2_account_number && (
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", py: 0.5 }}>銀行2口座番号</TableCell>
                        <TableCell sx={{ py: 0.5 }}>{owner.bank_2_account_number}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>

          {/* キャンセル時の案内 */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 2, lineHeight: 1.6 }}
          >
            ※ 別の新規家主として登録したい場合は「キャンセル」して家主名を変更してください。
          </Typography>
        </CardContent>

        <CardActions sx={{ justifyContent: "flex-end", p: 0, pt: 3, gap: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              fontWeight: "bold",
              borderRadius: "25px",
              color: "#9e9e9e",
              borderColor: "#9e9e9e",
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{
              whiteSpace: "nowrap",
              fontWeight: "bold",
              fontSize: "15px",
              backgroundColor: "rgba(64, 144, 255, 1)",
              border: "4px solid rgba(240, 232, 249, 0.9)",
              borderRadius: "25px",
              boxShadow: "4px 4px 8px rgba(0,0,0,0.3)",
              "&:hover": { backgroundColor: "rgba(64, 144, 255, 1)" },
            }}
          >
            既存データに紐づける
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

export default MasterConflictModal;
